/**
 * The trip filter shared by the globe's markers and its drawer's trip cards.
 *
 * Both sides have to agree on what "matches" means or the page contradicts
 * itself — a pin with no card, or the reverse. They read different sources
 * (`globe-view`'s trips JSON vs. a card's `data-*` attributes), so the
 * predicate lives here and each side adapts its own data to `FilterableTrip`.
 *
 * Imported by `lib/web-components/globe-view.ts`, so this module must stay free
 * of Preact and of anything server-only — it is bundled into `components.js`.
 */

/**
 * `startDate`/`endDate` bound `happened` as a half-open range —
 * inclusive start, exclusive end — matching the filter dialog's own
 * "on or after this date" / "before this date" labels.
 *
 * `tags` is an OR set: a trip matches when it carries any one of them. Tags are
 * expected lowercased, which `parseTripFilter` guarantees.
 */
export interface TripFilter {
  startDate?: string | null
  endDate?: string | null
  tags?: string[]
}

export interface FilterableTrip {
  /** `YYYY-MM-DD`, or null for a trip with no date. */
  happened?: string | null
  tags?: string[]
}

export function matchesTripFilter(
  trip: FilterableTrip,
  filter: TripFilter | null,
): boolean {
  if (!filter) return true

  if (filter.startDate || filter.endDate) {
    // `happened` is a plain `YYYY-MM-DD` string, so lexicographic ordering is
    // chronological ordering — no Date parsing, and no timezone to get wrong.
    const happened = trip.happened
    // An undated trip is not inside any range, so either bound excludes it.
    if (!happened) return false
    if (filter.startDate && happened < filter.startDate) return false
    if (filter.endDate && happened >= filter.endDate) return false
  }

  if (filter.tags && filter.tags.length > 0) {
    const own = (trip.tags ?? []).map((t) => t.toLowerCase())
    if (!filter.tags.some((t) => own.includes(t))) return false
  }

  return true
}

/**
 * Build a filter from the dialog's raw field values, or `null` when every field
 * is blank. An empty form meaning "no filter" is how the dialog clears itself,
 * so it needs no separate Clear button.
 */
export function parseTripFilter(
  fields: { start_date?: string; end_date?: string; tags?: string | string[] },
): TripFilter | null {
  const startDate = fields.start_date?.trim() ?? ""
  const endDate = fields.end_date?.trim() ?? ""
  // Two shapes, because the dialog's tag control changed but the predicate did
  // not: `MultiCombobox` submits one repeated `tags=` entry per selection, which
  // `FormData.getAll` hands over as an array. The comma-joined string is what the
  // free-text input produced, and is still what a hand-built filter reads most
  // naturally, so both stay supported rather than forcing callers to pre-join.
  const raw = Array.isArray(fields.tags) ? fields.tags : (fields.tags ?? "").split(",")
  const tags = raw
    .flatMap((t) => t.split(","))
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)

  if (!startDate && !endDate && tags.length === 0) return null
  return { startDate: startDate || null, endDate: endDate || null, tags }
}

/**
 * Coerce a `happened` value to the `YYYY-MM-DD` string the predicate compares.
 *
 * `getUserTopLevelTrips` selects `t.*`, so the postgres driver hands back a
 * `Date` for the `date` column even though `TripWithCounts.happened` is typed
 * `string | null` — the declared type is optimistic. Stringifying a `Date`
 * straight into a data attribute yields a millisecond timestamp, which then
 * sorts lexicographically against "2026-03-01" and quietly filters wrong.
 *
 * Built from local date parts rather than `toISOString()`: a `date` column
 * arrives as local midnight, and UTC-shifting it lands on the previous day for
 * any positive-offset zone.
 */
export function toISODate(value: unknown): string {
  if (!value) return ""
  if (value instanceof Date) {
    const pad = (n: number) => String(n).padStart(2, "0")
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
  }
  return String(value)
}
